// OOXML numbering (word/numbering.xml).
//
// Word stores list definitions in two tables:
//   <w:abstractNum w:abstractNumId="N">  — the reusable definition; one
//       <w:lvl w:ilvl="k"> per indent level, each with a <w:numFmt> (decimal,
//       lowerRoman, bullet, …), a <w:lvlText> (the glyph/pattern) and <w:start>.
//   <w:num w:numId="M">  — a concrete instance pointing at an abstractNumId,
//       optionally with <w:lvlOverride>/<w:startOverride> tweaks.
// A paragraph's <w:numPr> carries the numId + ilvl; we resolve that pair to a
// Plate listStyleType + start value.
import { attrByLocalName, childByLocalName, childrenByLocalName, val } from './readDocxXml';
import { BULLET_GLYPH_TO_LIST_STYLE, DEFAULT_LIST_START, NUM_FMT_TO_LIST_STYLE, OOXML_LIST_STYLE_TYPES } from './constants';
import type { ResolvedNumberingLevel } from './types';

interface LevelDef {
    numFmt?: string;
    lvlText?: string;
    start?: number;
}

export interface NumberingData {
    numToAbstract: Map<string, string>;
    abstractLevels: Map<string, Map<number, LevelDef>>;
    numOverrides: Map<string, Map<number, LevelDef>>;
}

function readLevel(lvlEl: Element): { ilvl: number; def: LevelDef } | null {
    const ilvlRaw = attrByLocalName(lvlEl, 'ilvl');
    const ilvl = ilvlRaw != null ? parseInt(ilvlRaw, 10) : NaN;
    if (!Number.isFinite(ilvl)) return null;
    const startRaw = val(childByLocalName(lvlEl, 'start'));
    const start = startRaw != null ? parseInt(startRaw, 10) : undefined;
    return {
        ilvl,
        def: {
            numFmt: val(childByLocalName(lvlEl, 'numFmt')) ?? undefined,
            lvlText: val(childByLocalName(lvlEl, 'lvlText')) ?? undefined,
            start: Number.isFinite(start as number) ? start : undefined,
        },
    };
}

export function parseNumbering(numberingDoc: Document | null): NumberingData {
    const numToAbstract = new Map<string, string>();
    const abstractLevels = new Map<string, Map<number, LevelDef>>();
    const numOverrides = new Map<string, Map<number, LevelDef>>();

    const root = numberingDoc?.documentElement ?? null;
    if (!root) return { numToAbstract, abstractLevels, numOverrides };

    for (const abstractNum of childrenByLocalName(root, 'abstractNum')) {
        const id = attrByLocalName(abstractNum, 'abstractNumId');
        if (id == null) continue;
        const levels = new Map<number, LevelDef>();
        for (const lvl of childrenByLocalName(abstractNum, 'lvl')) {
            const read = readLevel(lvl);
            if (read) levels.set(read.ilvl, read.def);
        }
        abstractLevels.set(id, levels);
    }

    for (const num of childrenByLocalName(root, 'num')) {
        const numId = attrByLocalName(num, 'numId');
        if (numId == null) continue;
        const abstractId = val(childByLocalName(num, 'abstractNumId'));
        if (abstractId != null) numToAbstract.set(numId, abstractId);

        const overrides = new Map<number, LevelDef>();
        for (const lvlOverride of childrenByLocalName(num, 'lvlOverride')) {
            const ilvlRaw = attrByLocalName(lvlOverride, 'ilvl');
            const ilvl = ilvlRaw != null ? parseInt(ilvlRaw, 10) : NaN;
            if (!Number.isFinite(ilvl)) continue;
            const startOverride = val(childByLocalName(lvlOverride, 'startOverride'));
            const nestedLvl = childByLocalName(lvlOverride, 'lvl');
            const nested = nestedLvl ? readLevel(nestedLvl)?.def : undefined;
            const start = startOverride != null ? parseInt(startOverride, 10) : nested?.start;
            overrides.set(ilvl, {
                numFmt: nested?.numFmt,
                lvlText: nested?.lvlText,
                start: Number.isFinite(start as number) ? start : undefined,
            });
        }
        if (overrides.size > 0) numOverrides.set(numId, overrides);
    }

    return { numToAbstract, abstractLevels, numOverrides };
}

function numFmtToListStyle(numFmt: string | undefined, lvlText: string | undefined): string {
    if (numFmt === 'bullet') {
        const glyph = (lvlText ?? '').trim().charAt(0);
        return BULLET_GLYPH_TO_LIST_STYLE[glyph] ?? OOXML_LIST_STYLE_TYPES.DISC;
    }
    return (numFmt && NUM_FMT_TO_LIST_STYLE[numFmt]) ?? OOXML_LIST_STYLE_TYPES.DECIMAL;
}

export function resolveNumberingLevel(numId: string, ilvl: number, data: NumberingData): ResolvedNumberingLevel | null {
    const abstractId = data.numToAbstract.get(numId);
    if (abstractId == null) return null;

    const base = data.abstractLevels.get(abstractId)?.get(ilvl);
    const override = data.numOverrides.get(numId)?.get(ilvl);

    const numFmt = override?.numFmt ?? base?.numFmt;
    if (numFmt == null) return null;

    const lvlText = override?.lvlText ?? base?.lvlText;
    const start = override?.start ?? base?.start ?? DEFAULT_LIST_START;

    return { listStyleType: numFmtToListStyle(numFmt, lvlText), start };
}
